import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classnames from 'classnames';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';

import messages from './messages';

import style from './style.module.less';

const formattedReturnNoteText = (noteText) => {
  return noteText.split('\n').map((line, i) => {
    return <div key={i}>{line}</div>;
  });
};

const ReturnNote = ({ noteBody = '', updateNote = noop, fulfillmentPlanId, hideAddNoteButton = false, className, id }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [noteBodyState, setNoteBodyState] = useState(noteBody || '');
  const [saving, setSaving] = useState(false);
  const textarea = useRef(null);
  const debouncedSetIsEditing = useRef(null);

  useEffect(() => {
    // clear saving flag when noteBody updates and not editing
    if (!isEditing) {
      if (!saving) {
        setNoteBodyState(noteBody);
      }
      setSaving(false);
    }
  }, [isEditing, noteBody, saving, setNoteBodyState, setSaving]);

  useEffect(() => {
    debouncedSetIsEditing.current = debounce((newIsEditing) => {
      setIsEditing(newIsEditing);
    });
  }, []);

  const onChange = useCallback(
    (e) => {
      setNoteBodyState(e.target.value);
    },
    [setNoteBodyState],
  );

  const changeEditState = useCallback(() => {
    const newIsEditing = !isEditing;
    debouncedSetIsEditing.current(newIsEditing);
  }, [isEditing, debouncedSetIsEditing]);

  useEffect(() => {
    if (isEditing && typeof textarea?.current?.focus === 'function') {
      textarea.current.focus();
    }
  }, [isEditing, textarea]);

  const saveNoteEdit = useCallback(() => {
    changeEditState();
    setSaving(true);
    updateNote(fulfillmentPlanId, noteBodyState);
  }, [changeEditState, fulfillmentPlanId, noteBodyState, updateNote]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        saveNoteEdit();
      }
    },
    [saveNoteEdit],
  );

  const shouldShowAddNoteButton = useMemo(() => {
    // this section adds some logic to make create/delete display conditions faster for the client
    const userEnteredNewText = noteBodyState !== '';
    const noteIsEmptyNowButWasNotBefore = noteBodyState === '';
    const noNoteExistedBeforeAndNotSaving = !saving;

    return !hideAddNoteButton && !isEditing && !userEnteredNewText && (noNoteExistedBeforeAndNotSaving || noteIsEmptyNowButWasNotBefore);
  }, [hideAddNoteButton, isEditing, noteBodyState, saving]);

  if (shouldShowAddNoteButton) {
    return (
      <div className={classnames(style['add-note-container'], className)}>
        <Button className={style['add-note']} onClick={changeEditState} theme='link'>
          <FormattedMessage {...messages.addNote} />
        </Button>
      </div>
    );
  }
  if (!isEditing) {
    return (
      <div className={classnames(style['return-note'], className)}>
        {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role */}
        <div className={classnames(style['return-note-text'], style.hovertext)} onFocus={changeEditState} role='button' tabIndex='0'>
          {formattedReturnNoteText(noteBodyState)}
        </div>
      </div>
    );
  }
  return (
    <div className={classnames(style['note-container'], className)}>
      <textarea
        ref={textarea}
        className={style['note-text-area']}
        id={id}
        onBlur={saveNoteEdit}
        onChange={onChange}
        onKeyDown={onKeyDown}
        value={noteBodyState}
      />
    </div>
  );
};

ReturnNote.propTypes = {
  noteBody: PropTypes.string,
  updateNote: PropTypes.func,
  fulfillmentPlanId: PropTypes.string.isRequired,
  hideAddNoteButton: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
};

export default ReturnNote;
